import {
  channelSchema,
  movementsQuerySchema,
  integrationStatusSchema,
  inventoryLocationPurposeSchema,
  inventoryMovementTypeSchema,
  matchingListSchema,
  matchingQuerySchema,
  organizationStateSchema,
  paginatedSchema,
  syncJobSchema,
  userRoleSchema,
  userSchema,
} from '@controlstock/contracts';
import type { integrationAuthorizationSchema } from '@controlstock/contracts';
import { z } from 'zod';

export { matchingListSchema, matchingQuerySchema };

export const syncJobsQuerySchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    channel: channelSchema.optional(),
    status: syncJobSchema.shape.status.optional(),
  })
  .strict();

export const usersQuerySchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    search: z.string().trim().max(120).optional(),
    role: userRoleSchema.optional(),
  })
  .strict();

export { movementsQuerySchema };

export const stockMovementSchema = z.object({
  id: z.string().uuid(),
  productVariantId: z.string().uuid(),
  inventoryLocationId: z.string().uuid(),
  sku: z.string().nullable(),
  productName: z.string().nullable(),
  type: inventoryMovementTypeSchema,
  previousStock: z.number().int(),
  delta: z.number().int(),
  resultingStock: z.number().int(),
  reason: z.string().nullable(),
  source: z.enum(['INTERNAL', 'TIENDANUBE', 'MERCADOLIBRE', 'RECONCILIATION']),
  channel: channelSchema.nullable(),
  locationPurpose: inventoryLocationPurposeSchema,
  externalReference: z.string().nullable(),
  createdAt: z.string().datetime(),
});

export const reconciliationHistoryQuerySchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    channel: channelSchema.optional(),
    status: z.enum(['PENDING', 'RUNNING', 'COMPLETED', 'FAILED', 'CANCELLED']).optional(),
  })
  .strict();

export const reconciliationRunSchema = z.object({
  id: z.string().uuid(),
  channel: channelSchema.nullable(),
  mode: z.enum(['INCREMENTAL', 'MANUAL', 'FULL']),
  status: z.enum(['PENDING', 'RUNNING', 'COMPLETED', 'FAILED', 'CANCELLED']),
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  lastError: z.string().nullable(),
  createdAt: z.string().datetime(),
});

// These endpoints currently return bounded arrays. Their filter schemas are
// retained for query-key stability and forward-compatible pagination in the UI.
export const syncJobsListSchema = syncJobSchema.array();
export const usersListSchema = userSchema.array();
export const integrationsListSchema = z.array(integrationStatusSchema);
export const movementsListSchema = paginatedSchema(stockMovementSchema);
export const reconciliationHistoryListSchema = paginatedSchema(reconciliationRunSchema);

export const queuedJobSchema = z.object({
  id: z.string().uuid(),
});

export const reconciliationRequestSchema = z.object({
  id: z.string().uuid(),
  status: z.literal('PENDING'),
});

export const onboardingSchema = z.object({
  state: organizationStateSchema,
  tiendanubeConnected: z.boolean(),
  mercadolibreConnected: z.boolean(),
  unresolvedAmbiguities: z.number().int().nonnegative(),
  catalogsImported: z.boolean(),
  stockSyncSkus: z.array(z.string()).default([]),
  stockSyncPublications: z.array(z.string()).default([]),
  mercadoLibreConnectionId: z.string().uuid().nullable().default(null),
  mercadoLibreStockPolicy: z.enum(['REFERENCE', 'MINIMUM']).default('REFERENCE'),
});

// The API intentionally does not allow a client to return an organization to
// SETUP after it has moved forward.
export const setOrganizationStateBodySchema = z
  .object({
    state: z.enum(['READ_ONLY', 'TRIAL', 'ACTIVE']),
    baselineHash: z
      .string()
      .regex(/^[a-f0-9]{64}$/)
      .optional(),
  })
  .strict();

export type MatchingQuery = z.output<typeof matchingQuerySchema>;
export type MatchingQueryInput = z.input<typeof matchingQuerySchema>;
export type SyncJobsQuery = z.output<typeof syncJobsQuerySchema>;
export type SyncJobsQueryInput = z.input<typeof syncJobsQuerySchema>;
export type UsersQuery = z.output<typeof usersQuerySchema>;
export type UsersQueryInput = z.input<typeof usersQuerySchema>;
export type MovementsQuery = z.output<typeof movementsQuerySchema>;
export type MovementsQueryInput = z.input<typeof movementsQuerySchema>;
export type ReconciliationHistoryQuery = z.output<typeof reconciliationHistoryQuerySchema>;
export type ReconciliationHistoryQueryInput = z.input<typeof reconciliationHistoryQuerySchema>;

export type MatchingList = z.output<typeof matchingListSchema>;
export type SyncJobsList = z.output<typeof syncJobsListSchema>;
export type UsersList = z.output<typeof usersListSchema>;
export type IntegrationsList = z.output<typeof integrationsListSchema>;
export type MovementsList = z.output<typeof movementsListSchema>;
export type ReconciliationHistoryList = z.output<typeof reconciliationHistoryListSchema>;
export type QueuedJob = z.output<typeof queuedJobSchema>;
export type ReconciliationRequest = z.output<typeof reconciliationRequestSchema>;
export type IntegrationAuthorization = z.output<typeof integrationAuthorizationSchema>;
export type Onboarding = z.output<typeof onboardingSchema>;
export type SetOrganizationStateInput = z.input<typeof setOrganizationStateBodySchema>;
